<template>
  <div class="payments-page">

    <base-navigation />

    <!-- Header -->
    <div class="page-header">
      <div>
        <h2>Loan Payments</h2>
        <p class="subtitle">Track all loan repayments and balances</p>
      </div>

      <button @click="fetchPayments" class="refresh-btn">
        ⟳ Refresh
      </button>
    </div>

    <!-- Summary cards -->
    <div class="summary">

      <div class="card total">
        <div class="card-title">Total Payments</div>
        <div class="card-value">
          {{ formatCurrency(totalAmount) }}
        </div>
      </div>

      <div class="card count">
        <div class="card-title">Transactions</div>
        <div class="card-value">
          {{ payments.length }}
        </div>
      </div>

      <div class="card latest">
        <div class="card-title">Last Payment</div>
        <div class="card-value small">
          {{ latestPayment }}
        </div>
      </div>

    </div>

    <!-- Loading -->
    <div v-if="loading" class="loading">
      Loading payments...
    </div>

    <!-- Table -->
    <div v-else class="table-card">

      <table class="payments-table">

        <thead>
          <tr>
            <th>ID</th>
            <th>Customer</th>
            <th>Account</th>
            <th>Amount</th>
            <th>Interest</th>
            <th>Principal</th>
            <th>Outstanding</th>
            <th>Transaction ID</th>
            <th>Date</th>
          </tr>
        </thead>

        <tbody>

          <tr v-for="payment in payments" :key="payment.id">

            <td>#{{ payment.id }}</td>

            <td>
              <span class="name">
                {{ payment.customer_name || "—" }}
              </span>
            </td>

            <td>
              <span class="account">
                {{ payment.account_number }}
              </span>
            </td>

            <td class="amount">
              {{ formatCurrency(payment.TransAmount) }}
            </td>

            <td class="interest">
              {{ formatCurrency(payment.interest || 0) }}
            </td>

            <td class="principal">
              {{ formatCurrency(payment.principal || 0) }}
            </td>

            <td class="balance">
              {{ formatCurrency(payment.outsandingAmount || 0) }}
            </td>

            <td>
              <span class="transid">
                {{ payment.TransID }}
              </span>
            </td>

            <td>
              {{ formatMpesaDate(payment.TransTime) }}
            </td>

          </tr>

        </tbody>

      </table>

    </div>

  </div>
</template>

<script>
import axios from "axios";

export default {

  name: "LoanPayments",

  data() {
    return {

      payments: [],
      loading: false

    };
  },

  computed: {

    totalAmount() {
      return this.payments.reduce(
        (sum, p) => sum + Number(p.TransAmount || 0),
        0
      );
    },

    latestPayment() {

      if (!this.payments.length) return "—";

      return this.formatMpesaDate(this.payments[0].TransTime);

    }

  },

  mounted() {
    this.fetchPayments();
  },

  methods: {

    async fetchPayments() {

      this.loading = true;

      try {

        const response = await axios.get(
          "https://api.roberms.com/raisetrack/get/loan/payments"
        );

        this.payments = response.data;

      }
      catch (error) {

        console.error(error);

      }
      finally {

        this.loading = false;

      }

    },

    formatCurrency(value) {

      return new Intl.NumberFormat("en-KE", {
        style: "currency",
        currency: "KES"
      }).format(value || 0);

    },

    formatMpesaDate(transTime) {

      if (!transTime) return "—";

      const year = transTime.substring(0, 4);
      const month = transTime.substring(4, 6);
      const day = transTime.substring(6, 8);
      const hour = transTime.substring(8, 10);
      const minute = transTime.substring(10, 12);

      return `${day}/${month}/${year} ${hour}:${minute}`;

    }

  }

};
</script>

<style scoped>

.payments-page {
  padding: 20px;
  background: #f4f7fb;
  min-height: 100vh;
}

/* header */

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 18px;
}

.subtitle {
  color: #6b7280;
  font-size: 13px;
}

.refresh-btn {
  background: linear-gradient(135deg, #2563eb, #1d4ed8);
  border: none;
  color: white;
  padding: 9px 18px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
}

.refresh-btn:hover {
  opacity: 0.9;
}

/* summary cards */

.summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
}

.card {
  padding: 18px;
  border-radius: 12px;
  background: white;
  box-shadow: 0 4px 14px rgba(0,0,0,0.05);
}

.card-title {
  color: #6b7280;
  font-size: 13px;
}

.card-value {
  font-size: 22px;
  font-weight: bold;
  margin-top: 6px;
}

.card-value.small {
  font-size: 16px;
}

/* table */

.table-card {
  background: white;
  border-radius: 12px;
  padding: 10px;
  box-shadow: 0 4px 14px rgba(0,0,0,0.05);
}

.payments-table {
  width: 100%;
  border-collapse: collapse;
}

.payments-table th {
  background: #f1f5f9;
  padding: 12px;
  font-size: 13px;
  text-align: left;
}

.payments-table td {
  padding: 12px;
  border-bottom: 1px solid #eee;
}

.payments-table tr:hover {
  background: #f9fafb;
}

/* styling */

.amount {
  font-weight: bold;
  color: #16a34a;
}

.balance {
  font-weight: bold;
  color: #dc2626;
}

.interest {
  color: #ea580c;
}

.principal {
  color: #2563eb;
}

.transid {
  font-family: monospace;
  font-size: 12px;
  background: #eef2ff;
  padding: 4px 7px;
  border-radius: 6px;
}

.account {
  font-weight: 600;
}

.name {
  font-weight: 600;
}

.loading {
  padding: 20px;
  text-align: center;
}

</style>